import { TRACKING_DESCRIPTION_IDS } from 'defs/tracking';
import * as React from 'react';

import { EventLevels, EventTypes, EventWarningCodes } from 'defs/events';
import Sequences from 'defs/sequences';

import Link from 'components/atoms/Link';
import withSequenceState from 'components/hoc/withSequenceState';
import ButtonBar from 'components/molecules/ButtonBar';
import InputGroup from 'components/molecules/InputGroup';
import Measurement from 'components/molecules/Measurement';

import { warn } from 'helper/debug';
import { getMobileDevicePlatform, iOSScrollKeyboardPositionFix, PLATFORMS } from 'helper/device';
import i18n from 'helper/i18n';

import withValidation from 'components/hoc/withValidation';
import sanitize from 'helper/sanitizer';
import bodyfatValidatorFactory from 'helper/validator/measurement-bodyfat';
import circumferenceValidatorFactory from 'helper/validator/measurement-circumference';
import weightValidatorFactory from 'helper/validator/measurement-weight';
import { exceedsTenPercentDifference } from 'helper/validator/weight';

import { LinkProps } from 'components/atoms/Link';
import { CardDetailType } from 'components/molecules/Card/Detail/CardDetailTypes';
import { InputGroupProps } from 'components/molecules/InputGroup';
import { MeasuringType } from 'components/molecules/Measurement';
import { TrackingDescriptionIDType } from 'defs/tracking';
import { CoachType } from 'types/coach';
import { ButtonProps } from '../../Button/Button';

const ControlledInputWeight = withValidation(InputGroup, weightValidatorFactory, [
	sanitize.stripChars,
	sanitize.replaceCommasWithDots,
	sanitize.allowFloat,
	sanitize.maxLengthWithDot(3, 2),
]);

const ControlledInputWaist = withValidation(InputGroup, circumferenceValidatorFactory, [
	sanitize.stripChars,
	sanitize.replaceCommasWithDots,
	sanitize.allowFloat,
	sanitize.maxLengthWithDot(3, 2),
]);

const ControlledInputBodyFat = withValidation(InputGroup, bodyfatValidatorFactory, [
	sanitize.stripChars,
	sanitize.replaceCommasWithDots,
	sanitize.allowFloat,
	sanitize.maxLengthWithDot(2, 2),
]);

export type MeasurementType = MeasuringType & {
	waist?: number;
	bodyFat?: number;
};

export type CardDetailTrackingProps = CardDetailType & {
	title?: string;
	coach: CoachType;
	currentWeight: number;
	measurement?: MeasurementType | null;
	profileMeasurement?: MeasurementType | null;
	inputWeight: {
		input: InputGroupProps;
	};
	inputWaist: {
		input: InputGroupProps;
	};
	inputBodyFat: {
		input: InputGroupProps;
	};
	infoText?: string;
	link?: LinkProps;
	isUpdatingTracking: boolean;
	isDone: boolean;
	doSaveTracking: Function;
	doUpdateTracking: Function;
	doLoadUserData: Function; // store/profile => actionCreators
	isSequenceLoading: boolean;

	trackCardTrackingSave: () => void;
	doAddEvents: (events: Object[]) => void;
	doOpenDescription: (guideId: TrackingDescriptionIDType) => void;
};

export type CardDetailTrackingState = {
	isEditing: boolean;
	hasValidWeight: boolean;
	hasValidWaist: boolean;
	hasValidBodyFat: boolean;
	inputValueWeight: string;
	inputValueWaist: string;
	inputValueBodyFat: string;
};

export type IosScrollFixType = {
	renderDetailSpacer: () => any;
};

export class CardDetailTracking extends React.Component<CardDetailTrackingProps, CardDetailTrackingState> {
	get areInputsValid(): boolean {
		return this.state.hasValidWeight && this.state.hasValidWaist && this.state.hasValidBodyFat;
	}

	get isIOS(): boolean {
		return getMobileDevicePlatform() === PLATFORMS.IOS;
	}
	public static displayName = 'CardDetailTracking';

	public static defaultProps = {
		coach: '',
		currentWeight: 0,
		isSequenceLoading: false,
		title: i18n('react.impulsCoach.components.cardDetail.tracking.title'),
		infoText: i18n('react.impulsCoach.components.cardDetail.tracking.infoText'),
	};

	/*
	 *public * DIRTY of iscroll
	 * FIXME: maybe this could be removed after iPhone 6 is not used anymore
	 * @see jira.migros.net/browse/PULSWM-2992
	 * @see jira.migros.net/browse/PULSWM-3117
	 */
	public iosScrollFix: IosScrollFixType = {
		renderDetailSpacer: () => {
			const { profileMeasurement, uiState } = this.props;
			if (!this.isIOS || uiState !== 'detail') {
				return null;
			}
			if (this.state.isEditing && profileMeasurement && profileMeasurement.weight) {
				return null;
			}
			if (this.state.isEditing) {
				return <div style={{ height: 63 }} />;
			}
			return <div style={{ height: 370 }} />;
		},
	};

	// 	props: CardDetailTrackingProps;
	// 	state: CardDetailTrackingState;
	// FIX: Use spacers because
	constructor(props: CardDetailTrackingProps) {
		super(props);

		const measurement: any = this.props.measurement || {};

		const measuredData = {
			inputValueWaist: measurement.waist ? measurement.waist.toString() : '',
			inputValueWeight: measurement.weight ? measurement.weight.toString() : '',
			inputValueBodyFat: measurement.bodyFat ? measurement.bodyFat.toString() : '',
		};

		const measuredValidStates = {
			hasValidWeight: !!(measuredData.inputValueWeight.length > 0),
		};

		this.state = {
			isEditing: false,

			hasValidWaist: true,
			hasValidBodyFat: true,
			...measuredData,
			...measuredValidStates,
			hasValidWeight: false,
		};
	}

	public componentDidMount() {
		const { measurement } = this.props;

		if (!measurement || !measurement.weight) {
			this.setState({
				isEditing: true,
			});
		}
	}

	public componentDidUpdate(prevProps: CardDetailTrackingProps) {
		const { isDone } = this.props;

		if (isDone && !this.state.isEditing) {
			this.setState({
				isEditing: false,
			});
		}
	}

	public handleButtonClicksWithoutValidation = (buttonType: string) => {
		const { isDone, isSequenceLoading } = this.props;

		if (isSequenceLoading) {
			return;
		}

		switch (buttonType) {
			case 'completed':
				if (isDone) {
					this.props.handleCompleteCard();
					this.props.doUpdateTracking(this.props.id, {
						weight: this.state.inputValueWeight,
						waist: this.state.inputValueWaist,
						bodyFat: this.state.inputValueBodyFat,
					});
					this.props.trackCardTrackingSave();
				} else {
					this.props.onComplete({
						type: 'tracking',
						onAnimationDone: () => {
							this.props.doSaveTracking(this.props.id, {
								weight: this.state.inputValueWeight,
								waist: this.state.inputValueWaist,
								bodyFat: this.state.inputValueBodyFat,
							});
							this.props.trackCardTrackingSave();
						},
					});
				}
				break;
			case 'close':
				const { waist, weight, bodyFat } = this.props.measurement || ({} as any);

				this.props.handleCompleteCard();
				this.setState({
					isEditing: false,
					inputValueWaist: waist ? waist.toString() : '',
					inputValueWeight: weight ? weight.toString() : '',
					inputValueBodyFat: bodyFat ? bodyFat.toString() : '',
				});
				break;
			default:
				warn(`Button Action "${buttonType}" not available`);
				break;
		}
	};

	public handleButtonClicks = (buttonType: string) => {
		const { currentWeight } = this.props;
		const { inputValueWeight } = this.state;

		if (exceedsTenPercentDifference(currentWeight, parseFloat(inputValueWeight))) {
			return this.renderWeightDifferenceNotification();
		}

		this.handleButtonClicksWithoutValidation(buttonType);
	};

	public handleInputWeight = (inputValue: number | string) => {
		this.setState({
			inputValueWeight: inputValue.toString(),
		});
	};

	public handleInputWaist = (inputValue: number | string) => {
		this.setState({
			inputValueWaist: inputValue.toString(),
		});
	};

	public handleInputBodyFat = (inputValue: number | string) => {
		this.setState({
			inputValueBodyFat: inputValue.toString(),
		});
	};

	public handleValidInputWeight = (errorType?: string) => {
		this.setState({
			hasValidWeight: !Boolean(typeof errorType === 'string'),
		});
	};

	public handleValidInputWaist = (errorType?: string) => {
		this.setState({
			hasValidWaist: !Boolean(typeof errorType === 'string'),
		});
	};

	public handleValidInputBodyFat = (errorType?: string) => {
		this.setState({
			hasValidBodyFat: !Boolean(typeof errorType === 'string'),
		});
	};

	public toggleEditState = () => {
		this.setState({
			isEditing: !this.state.isEditing,
		});
	};

	public statefulLinkText() {
		if (this.props.uiState === 'reopened') {
			return i18n('react.impulsCoach.components.cardDetail.tracking.linkText.resolved');
		}

		return i18n('react.impulsCoach.components.cardDetail.tracking.linkText.unresolved');
	}

	public getInputLabel(field: string, type: string): string {
		return i18n(`react.impulsCoach.components.cardDetail.tracking.${field}.${type}`);
	}

	public renderButtonBar() {
		const { isDone, uiState, isCheckable, isUpdatingTracking, isSequenceLoading } = this.props;
		const { isEditing } = this.state;
		let cardButtons: any = null;

		if (!isCheckable) {
			return null;
		}

		if (uiState === 'detail') {
			if (!isDone && isEditing) {
				cardButtons = {
					buttonsLayout: 'shift-right',
					buttons: [
						{
							type: this.areInputsValid ? '' : 'disabled',
							labelBottom: i18n('react.impulsCoach.components.cardDetail.tracking.buttons.save'),
							knob: {
								modifier: 'dashed',
								icon: 'completed',
								size: 'l',
							},
						},
					],
				} as ButtonProps;
			}

			if (isDone && isEditing) {
				cardButtons = {
					buttonsLayout: 'shift-right',
					buttons: [
						{
							type: this.areInputsValid ? '' : 'disabled',
							labelBottom: i18n('react.impulsCoach.components.cardDetail.tracking.buttons.update'),
							knob: {
								modifier: 'dashed',
								icon: 'completed',
								size: 'l',
							},
						},
						{
							labelBottom: i18n('react.impulsCoach.components.cardDetail.tracking.buttons.cancel'),
							knob: {
								modifier: 'dashed',
								icon: 'close',
								size: 'l',
							},
						},
					],
				} as ButtonProps;
			}
		}

		if (cardButtons === null) {
			return null;
		}

		return (
			<div className="m-card__button-bar">
				<ButtonBar
					loading={isUpdatingTracking || this.props.isUiCompleting}
					disabled={isSequenceLoading}
					handleButtonClicks={this.handleButtonClicks}
					buttons={cardButtons.buttons}
				/>
			</div>
		);
	}

	public renderWeightDifferenceNotification(): void {
		const weightUnit = i18n('react.impulsCoach.events.warning.w02.na.weightUnit');

		this.props.doAddEvents([
			{
				eventLevel: EventLevels.NotAvailable,
				code: EventWarningCodes.W02,
				eventType: EventTypes.Warning,
				overwrites: {
					title: i18n('react.impulsCoach.events.warning.w02.na.title'),
					text: (
						<div
							dangerouslySetInnerHTML={{
								__html: i18n('react.impulsCoach.events.warning.w02.na.text', {
									__disableHtmlEncoding: true,
									weight: `<strong>${this.state.inputValueWeight} ${weightUnit}</strong>`,
								}),
							}}
						/>
					),
				},
				callback: (callbackId: string, done: Function) => {
					if (callbackId === 'proceed') {
						this.handleButtonClicksWithoutValidation('completed');
					}
					done();
				},
			},
		]);
	}

	public render() {
		const {
			handleCardContentClick,
			handleShowDetailsClick,
			measurement,
			profileMeasurement,
			inputWeight,
			inputWaist,
			inputBodyFat,
			infoText,
			title,
		} = this.props;

		const { inputValueWeight, inputValueWaist, inputValueBodyFat }: CardDetailTrackingState = this.state;
		const measurementShowText = !!(measurement && measurement.weight);

		return (
			<div>
				<div className="m-card__content" onClick={handleCardContentClick}>
					<div className="m-card__info">
						<h4 className="m-card__info-title">
							{title || i18n('react.impulsCoach.components.cardDetail.tracking.title')}
						</h4>
						<div className="m-card__info-line">
							{infoText && (
								<p className="m-card__info-text">
									{infoText || i18n('react.impulsCoach.components.cardDetail.tracking.infoText')}
								</p>
							)}
						</div>
					</div>
					<div className="m-card__link js-m-card__link">
						<Link
							onClick={handleShowDetailsClick}
							title={this.statefulLinkText()}
							modifier="button"
							openInNewWindow={false}
							iconRight={null}
						/>
					</div>
					<div className="m-card__detail">
						{!this.state.isEditing && measurement && measurement.weight ? (
							<Measurement
								modifier="editable"
								measuring={{
									weight: measurement.weight,
									time: measurement.time || ' ',
									date: measurement.date,
								}}
								onEdit={this.toggleEditState}
								showText={measurementShowText}
							/>
						) : null}
						{this.state.isEditing && (
							<div>
								{profileMeasurement && profileMeasurement.weight && (
									<Measurement
										measuring={{
											weight: profileMeasurement.weight,
											time: profileMeasurement.time || ' ',
											date: profileMeasurement.date,
										}}
										showText={measurementShowText}
									/>
								)}
								<ControlledInputWeight
									{...inputWeight.input}
									value={inputValueWeight}
									suffix={this.getInputLabel('bodyWeight', 'suffix')}
									text={this.getInputLabel('bodyWeight', 'text')}
									label={this.getInputLabel('bodyWeight', 'title')}
									onChange={this.handleInputWeight as any}
									onBlur={iOSScrollKeyboardPositionFix}
									onValidationDone={this.handleValidInputWeight as any}
									actionKnob={
										{
											callback: () =>
												this.props.doOpenDescription(TRACKING_DESCRIPTION_IDS.WEIGHT as any),
										} as any
									}
								/>
								<ControlledInputWaist
									{...inputWaist.input}
									value={inputValueWaist}
									suffix={this.getInputLabel('bodyCircumference', 'suffix')}
									text={this.getInputLabel('bodyCircumference', 'text')}
									label={this.getInputLabel('bodyCircumference', 'title')}
									onChange={this.handleInputWaist as any}
									onBlur={iOSScrollKeyboardPositionFix}
									onValidationDone={this.handleValidInputWaist as any}
									actionKnob={
										{
											callback: () =>
												this.props.doOpenDescription(TRACKING_DESCRIPTION_IDS.WAIST as any),
										} as any
									}
								/>

								<ControlledInputBodyFat
									{...inputBodyFat.input}
									value={inputValueBodyFat}
									suffix={this.getInputLabel('bodyFat', 'suffix')}
									text={this.getInputLabel('bodyFat', 'text')}
									label={this.getInputLabel('bodyFat', 'title')}
									onChange={this.handleInputBodyFat as any}
									onBlur={iOSScrollKeyboardPositionFix}
									onValidationDone={this.handleValidInputBodyFat as any}
									actionKnob={
										{
											callback: () =>
												this.props.doOpenDescription(TRACKING_DESCRIPTION_IDS.BODYFAT as any),
										} as any
									}
								/>
							</div>
						)}
					</div>
					{this.renderButtonBar()}
					{this.iosScrollFix.renderDetailSpacer()}
				</div>
			</div>
		);
	}
}

export default withSequenceState(CardDetailTracking as any, [Sequences.CARD_IS_CHECKING]);
